<template>
  <div class="bg-surface-default-low w-full md:max-w-[360px]">
    <div class="flex flex-col">
      <div class="flex items-center gap-12">
        <RevIllustration
          :alt="product.title"
          :height="40"
          sizes="40px"
          :src="product.imageUrl"
          :width="40"
        />
        <p class="body-1-bold text-left">{{ product.title }}</p>
        <RevButton
          v-if="hasEdit"
          :icon="IconEdit"
          size="small"
          variant="secondary"
          @click="emit('edit')"
        >
          {{ i18n(translations.edit) }}
        </RevButton>
      </div>

      <div
        v-if="gradeData"
        class="bg-surface-default-hi rounded-md mt-16 flex items-center justify-between p-8 px-12"
      >
        <p class="text-static-default-low body-2">
          {{ i18n(translations.productDetailsCondition) }}
        </p>

        <div class="flex items-center gap-4">
          <p class="text-action-default-hi body-1-bold">{{ gradeData.name }}</p>
          <RevTooltip :modifiers="TOOLTIP_MODIFIER" position="bottom">
            <template #trigger="{ show, hide }">
              <IconInfo
                size="20"
                @click="show"
                @focusin="show"
                @focusout="hide"
                @mouseenter="show"
                @mouseleave="hide"
              />
            </template>
            {{ gradeData.description }}
          </RevTooltip>
        </div>
      </div>

      <div
        v-if="averageRate"
        class="bg-surface-default-hi rounded-md mt-12 flex items-center justify-between p-8 px-12"
      >
        <p class="text-static-default-low body-2">
          {{ i18n(translations.rating) }}
        </p>

        <RevRating :score="averageRate" size="small" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Grade } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'
import { RevRating } from '@ds/components/Rating'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconEdit } from '@ds/icons/IconEdit'
import { IconInfo } from '@ds/icons/IconInfo'

import type { Product } from '~/api/reviews/types/GetReviewCollectionResponse'

import translations from './ProductSummary.translations'

const i18n = useI18n()

const props = withDefaults(
  defineProps<{
    product: Product
    backboxGrade?: Grade
    averageRate?: number
    hasEdit?: boolean
  }>(),
  {
    backboxGrade: undefined,
    averageRate: undefined,
    hasEdit: false,
  },
)

const emit = defineEmits<{ (e: 'edit'): void }>()

const TOOLTIP_MODIFIER = [
  {
    name: 'offset',
    options: {
      offset: [-95, 0],
    },
  },
]

const gradeData = computed(() => {
  switch (props.backboxGrade?.name) {
    case 'ECO':
      return {
        name: i18n(translations.ecoGrade),
        description: i18n(translations.ecoGradeDescription),
      }
    case 'VERY_GOOD':
      return {
        name: i18n(translations.veryGoodGrade),
        description: i18n(translations.veryGoodGradeDescription),
      }
    case 'FLAWLESS':
      return {
        name: i18n(translations.flawlessGrade),
        description: i18n(translations.flawlessGradeDescription),
      }
    case 'PREMIUM':
      return {
        name: i18n(translations.premiumGrade),
        description: i18n(translations.premiumGradeDescription),
      }
    default:
      return null
  }
})
</script>
